import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ageOn, travelerFormSchema, type TravelerForm } from '@/lib/lcc/forms';
import { isAdultMember, type LccPartyMember, type LccTraveler } from '@/lib/lcc/types';

interface Props {
  passengers: LccPartyMember[];
  departureDate: string;
  disabled: boolean;
  onReview: (travelers: LccTraveler[]) => Promise<void>;
  initialTravelers?: LccTraveler[] | undefined;
  /** Optional per-passenger caption (e.g. "Adult", "Child · 7"), keyed by passenger id. */
  captions?: Record<string, string> | undefined;
}
const field =
  'w-full rounded-md border border-border bg-background px-2.5 py-2 text-sm font-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
// Adults search by type only; their date of birth must make them 18+ on departure.
const ADULT_MIN_AGE = 18;
const carriesInfant = (p: LccPartyMember) => isAdultMember(p) || p.age >= 18;
const isInfant = (p: LccPartyMember) => !isAdultMember(p) && p.age < 2;

export function LccTravelers({
  passengers,
  departureDate,
  disabled,
  onReview,
  initialTravelers,
  captions,
}: Props) {
  const { t } = useTranslation('lcc');
  const [ageMismatch, setAgeMismatch] = useState(false);
  const [adultTooYoung, setAdultTooYoung] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TravelerForm>({
    resolver: zodResolver(travelerFormSchema),
    defaultValues: {
      travelers: passengers.map((p) => {
        const initial = initialTravelers?.find((person) => person.id === p.id);
        return initial
          ? {
              ...initial,
              nationality: initial.nationality ?? '',
              infantPassengerId: initial.infantPassengerId ?? '',
              passportNumber: initial.passport?.number ?? '',
              passportExpiry: initial.passport?.expiresOn ?? '',
              passportCountry: initial.passport?.issuingCountry ?? '',
            }
          : {
              id: p.id,
              givenName: '',
              familyName: '',
              bornOn: '',
              email: '',
              phoneNumber: '',
              nationality: '',
              passportNumber: '',
              passportExpiry: '',
              passportCountry: '',
              infantPassengerId: '',
            };
      }),
    },
  });

  async function submit(data: TravelerForm) {
    const ageOf = (p: TravelerForm['travelers'][number]) => ageOn(p.bornOn, departureDate);
    const member = (id: string) => passengers.find((v) => v.id === id);
    const mismatch = data.travelers.some((p) => {
      const m = member(p.id);
      return !m || (!isAdultMember(m) && ageOf(p) !== m.age);
    });
    const tooYoung = data.travelers.some((p) => {
      const m = member(p.id);
      return !!m && isAdultMember(m) && ageOf(p) < ADULT_MIN_AGE;
    });
    setAgeMismatch(mismatch);
    setAdultTooYoung(tooYoung);
    if (mismatch || tooYoung) return;
    await onReview(
      data.travelers.map(
        ({
          nationality,
          passportNumber,
          passportExpiry,
          passportCountry,
          infantPassengerId,
          ...p
        }) => ({
          ...p,
          ...(nationality ? { nationality } : {}),
          ...(infantPassengerId ? { infantPassengerId } : {}),
          ...(passportNumber
            ? {
                passport: {
                  number: passportNumber,
                  expiresOn: passportExpiry,
                  issuingCountry: passportCountry,
                },
              }
            : {}),
        }),
      ),
    );
  }

  return (
    <form className="space-y-3" onSubmit={handleSubmit(submit)} autoComplete="off">
      <p className="text-muted-foreground">{t('travelerHelp')}</p>
      {passengers.map((p, i) => (
        <fieldset
          key={p.id}
          disabled={disabled}
          className="grid gap-3 rounded-lg border border-border bg-background p-4 sm:grid-cols-2 xl:grid-cols-3"
        >
          <legend className="px-1 font-medium">
            {t('traveler', { n: i + 1 })}
            {captions?.[p.id] ? (
              <span className="font-normal text-muted-foreground"> · {captions[p.id]}</span>
            ) : null}
          </legend>
          <input type="hidden" {...register(`travelers.${i}.id`)} />
          <label className="grid gap-1 text-sm font-medium">
            {t('givenName')}
            <input className={field} {...register(`travelers.${i}.givenName`)} required />
          </label>
          <label className="grid gap-1 text-sm font-medium">
            {t('familyName')}
            <input className={field} {...register(`travelers.${i}.familyName`)} required />
          </label>
          <label className="grid gap-1 text-sm font-medium">
            {t('bornOn')}
            <input className={field} type="date" {...register(`travelers.${i}.bornOn`)} required />
          </label>
          <label className="grid gap-1 text-sm font-medium">
            {t('gender')}
            <select
              className={field}
              {...register(`travelers.${i}.gender`)}
              required
              defaultValue=""
            >
              <option value="">{t('chooseValue')}</option>
              <option value="m">{t('male')}</option>
              <option value="f">{t('female')}</option>
            </select>
          </label>
          <label className="grid gap-1 text-sm font-medium">
            {t('salutation')}
            <select
              className={field}
              {...register(`travelers.${i}.title`)}
              required
              defaultValue=""
            >
              <option value="">{t('chooseValue')}</option>
              {['mr', 'ms', 'mrs', 'miss', 'dr'].map((v) => (
                <option key={v} value={v}>
                  {t(`salutations.${v}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-sm font-medium">
            {t('nationality')}
            <input
              className={field}
              {...register(`travelers.${i}.nationality`)}
              maxLength={2}
              pattern="[A-Z]{2}"
              placeholder="GB"
            />
          </label>
          <label className="grid gap-1 text-sm font-medium">
            {t('email')}
            <input className={field} type="email" {...register(`travelers.${i}.email`)} required />
          </label>
          <label className="grid gap-1 text-sm font-medium">
            {t('phone')}
            <input
              className={field}
              type="tel"
              {...register(`travelers.${i}.phoneNumber`)}
              required
              placeholder="+442012345678"
            />
          </label>
          <label className="grid gap-1 text-sm font-medium">
            {t('passportNumber')}
            <input className={field} {...register(`travelers.${i}.passportNumber`)} />
          </label>
          <label className="grid gap-1 text-sm font-medium">
            {t('passportExpiry')}
            <input className={field} type="date" {...register(`travelers.${i}.passportExpiry`)} />
          </label>
          <label className="grid gap-1 text-sm font-medium">
            {t('passportCountry')}
            <input
              className={field}
              {...register(`travelers.${i}.passportCountry`)}
              maxLength={2}
              pattern="[A-Z]{2}"
              placeholder="GB"
            />
          </label>
          {carriesInfant(p) && passengers.some(isInfant) ? (
            <label className="grid gap-1 text-sm font-medium">
              {t('infant')}
              <select className={field} {...register(`travelers.${i}.infantPassengerId`)}>
                <option value="">{t('none')}</option>
                {passengers.map((infant, index) =>
                  isInfant(infant) ? (
                    <option key={infant.id} value={infant.id}>
                      {t('traveler', { n: index + 1 })}
                    </option>
                  ) : null,
                )}
              </select>
            </label>
          ) : null}
        </fieldset>
      ))}
      {errors.travelers ? (
        <p role="alert" className="text-destructive">
          {t('invalidTravelers')}
        </p>
      ) : null}
      {ageMismatch ? (
        <p role="alert" className="text-destructive">
          {t('ageMismatch')}
        </p>
      ) : null}
      {adultTooYoung ? (
        <p role="alert" className="text-destructive">
          {t('adultTooYoung', { ns: 'reservations' })}
        </p>
      ) : null}
      <Button type="submit" size="sm" disabled={disabled}>
        {t('review')}
      </Button>
    </form>
  );
}
